import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const emptyForm = { email: "", name: "", role: "warden", password: "" };

export default function Users() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [resetFor, setResetFor] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const load = () => api.get("/users").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post("/users", form);
      toast.success(`${form.role.toUpperCase()} account created`);
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post(`/users/${resetFor.id}/reset-password`, { password: newPassword });
      toast.success("Password reset");
      setResetFor(null); setNewPassword("");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const roleBadge = (r) => ({
    admin: "default", warden: "secondary", accountant: "secondary", resident: "outline",
  }[r] || "outline");

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-muted-foreground">Administration</div>
          <h1 className="font-display text-4xl font-bold mt-1">Users & Access</h1>
          <p className="text-muted-foreground mt-1">
            Invite wardens, accountants and residents. Only admins see this page.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn"><Plus className="w-4 h-4 mr-2" /> New User</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display text-2xl">Create User</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="user-name" />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input required type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="user-email" />
              </div>
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin (Super Admin)</SelectItem>
                    <SelectItem value="director">Director</SelectItem>
                    <SelectItem value="hostel_manager">Hostel Manager</SelectItem>
                    <SelectItem value="warden">Warden</SelectItem>
                    <SelectItem value="accountant">Accountant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Initial Password *</Label>
                <Input required type="text" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="user-password"
                  placeholder="Min 6 characters — share with the user securely" />
                <p className="text-xs text-muted-foreground">
                  Ask the user to change this on first sign-in.
                </p>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="user-save">Create User</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="text-left text-muted-foreground eyebrow border-b border-border">
                <th className="py-3 pr-4">Name</th>
                <th className="py-3 pr-4">Email</th>
                <th className="py-3 pr-4">Role</th>
                <th className="py-3 pr-4">Created</th>
                <th className="py-3 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((u) => (
                <tr key={u.id} className="border-b border-border/50 hover:bg-secondary/40">
                  <td className="py-3 pr-4 font-medium">
                    {u.name}
                    {u.id === user?.id && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}
                  </td>
                  <td className="py-3 pr-4 font-mono text-xs">{u.email}</td>
                  <td className="py-3 pr-4">
                    <Badge variant={roleBadge(u.role)} className="uppercase text-[10px]">{u.role}</Badge>
                  </td>
                  <td className="py-3 pr-4 font-mono text-xs text-muted-foreground">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-3 pr-4 text-right">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => { setResetFor(u); setNewPassword(""); }}
                              data-testid={`reset-${u.id}`}>
                        <KeyRound className="w-3 h-3 mr-1" /> Reset
                      </Button>
                      {u.id !== user?.id && (
                        <Button size="sm" variant="destructive" onClick={() => del(u)}
                                data-testid={`delete-${u.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr><td colSpan={5} className="py-10 text-center text-muted-foreground">No users.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!resetFor} onOpenChange={(v) => !v && setResetFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Reset password for {resetFor?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={resetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label>New Password *</Label>
              <Input required type="text" value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="reset-password-input" />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="reset-password-save">Set Password</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
